import { useGetCurrentCartQuery } from '@api/cart.api';
import { CartItemWithDetails, Price } from '@api/types';
import { useAuth } from '@hooks';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type CartContextProps = {
  children: React.ReactNode;
};

type CartContextType = {
  cartItems?: CartItemWithDetails[];
  isWorking?: boolean;
  isEmpty?: boolean;
  cartTotal?: Price;
  cartSubTotal?: Price;
  cartTaxTotal?: Price;
  focusOnInput: boolean;
  setFocusOnInput: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({ children }: CartContextProps) => {
  const { isBeremaUser, isAuthenticated } = useAuth();
  const {
    currentData: currentCartData,
    isSuccess,
    isFetching,
    isLoading,
  } = useGetCurrentCartQuery(undefined, {
    skip: isBeremaUser || !isAuthenticated,
  });
  const [currentItems, setCurrentItems] = useState<CartItemWithDetails[]>([]);
  const [focusOnInput, setFocusOnInput] = useState<boolean>(false);

  const cart = currentCartData?.data ?? {};
  const defaultPrice: Price = { amount: 0, currency: 'NOK' };
  const {
    lineItems = [],
    total = defaultPrice,
    subTotal = defaultPrice,
    taxTotal = defaultPrice,
  } = cart;

  const isWorking = (isFetching || isLoading) && !isSuccess;
  const isEmpty = isSuccess && !isWorking && currentItems?.length === 0;

  useEffect(() => {
    if (lineItems && isSuccess && !isFetching && !isLoading) {
      setCurrentItems(lineItems);
    }
  }, [lineItems, isSuccess, isFetching, isLoading]);

  const contextValue = useMemo(
    () => ({
      cartItems: currentItems,
      isWorking,
      isEmpty,
      cartTotal: total,
      cartSubTotal: subTotal,
      cartTaxTotal: taxTotal,
      focusOnInput,
      setFocusOnInput,
    }),
    [currentItems, isWorking, isEmpty, total, subTotal, taxTotal, focusOnInput]
  );
  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within a CartContextProvider');
  }
  return context;
};
